import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
    hideAppSpinner,
    showAppNotification,
    showAppSpinner,
} from '@rainbow-modules/app';
import Button from 'components/Button';
import Plus from 'components/icons/plus';
import HeaderText from 'components/PageComponents/headerText';
import HeaderTextDescription from 'components/PageComponents/headerTextDescription';
import BasicPageLayout from 'layouts/BasicPage';
import InvoiceList from './InvoiceList';
import FilterButton from './InvoiceList/FilterButton';
import CreateInvoiceDrawer from './CreateInvoiceDrawer';
import EditInvoiceDrawer from './EditInvoiceDrawer';
import ViewInvoiceDrawer from './ViewInvoiceDrawer';
import printInvoice from './printInvoice';
import downloadInvoice from './downloadInvoice';
import { fetchInvoiceForOutput } from './api/client';
import { useInvoice } from './api/hooks';
import { InvoiceRow } from './types';
import {
    Header, HeaderActions, HeaderCopy, PageCard,
} from './styled';

const InvoiceBuilderPage = () => {
    const { agentId = '' } = useParams();
    const queryClient = useQueryClient();
    const [isCreateOpen, setCreateOpen] = useState(false);
    const [editInvoiceId, setEditInvoiceId] = useState<string | undefined>();
    const [viewInvoiceId, setViewInvoiceId] = useState<string | undefined>();

    const {
        data: invoiceResult,
        isLoading: isLoadingInvoice,
    } = useInvoice(agentId, viewInvoiceId);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries(['invoice-builder']);
    }, [queryClient]);

    const handlePrint = useCallback(async (row: InvoiceRow) => {
        showAppSpinner();
        try {
            const result = await fetchInvoiceForOutput(agentId, row.id);
            await printInvoice(result.invoice, result.acceptance, agentId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not print invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [agentId]);

    const handleDownload = useCallback(async (row: InvoiceRow) => {
        showAppSpinner();
        try {
            const result = await fetchInvoiceForOutput(agentId, row.id);
            await downloadInvoice(result.invoice, result.acceptance, agentId);
        } catch (error: any) {
            showAppNotification({
                title: 'Could not download invoice',
                description: error?.message || 'Please try again.',
                icon: 'error',
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [agentId]);

    return (
        <BasicPageLayout>
            <PageCard>
                <Header>
                    <HeaderCopy>
                        <HeaderText>Invoice Builder</HeaderText>
                        <HeaderTextDescription>
                            Create, view, and print invoices issued from enabled templates.
                        </HeaderTextDescription>
                    </HeaderCopy>
                    <HeaderActions>
                        <FilterButton
                            label="Filters"
                            organizationId={agentId}
                        />
                        <Button
                            variant="brand"
                            borderRadius="semi-rounded"
                            onClick={() => setCreateOpen(true)}
                        >
                            <Plus className="rainbow-m-right_small" />
                            Create invoice
                        </Button>
                    </HeaderActions>
                </Header>
                <InvoiceList
                    organizationId={agentId}
                    onView={(row) => setViewInvoiceId(row.id)}
                    onEdit={(row) => setEditInvoiceId(row.id)}
                    onPrint={handlePrint}
                    onDownload={handleDownload}
                />
            </PageCard>
            <CreateInvoiceDrawer
                isOpen={isCreateOpen}
                organizationId={agentId}
                onRequestClose={() => setCreateOpen(false)}
                onCreated={refreshList}
            />
            <ViewInvoiceDrawer
                isOpen={Boolean(viewInvoiceId)}
                organizationId={agentId}
                invoice={invoiceResult?.data}
                isLoading={isLoadingInvoice}
                onRequestClose={() => setViewInvoiceId(undefined)}
                onUpdated={refreshList}
                onEdit={(invoice) => setEditInvoiceId(invoice.id)}
            />
            <EditInvoiceDrawer
                isOpen={Boolean(editInvoiceId)}
                organizationId={agentId}
                invoiceId={editInvoiceId}
                onRequestClose={() => setEditInvoiceId(undefined)}
                onUpdated={refreshList}
            />
        </BasicPageLayout>
    );
};

export default InvoiceBuilderPage;
